import React, { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { customValue as Symbol_customValue } from '../symbols';
import { themeState } from '../theme';
import { optionsState } from '../options';
import { getTypeOf, typeNames, isAnonymousFunction, isArrowFunction } from '../utils/types';

function FunctionValue(_ref) {
  let {
    data,
    nameColor,
    typeColor,
    arrowColor,
    hideDataTypes
  } = _ref;
  const type = getTypeOf(data);
  const isAsyncFn = [typeNames.AsyncFunction, typeNames.AsyncGeneratorFunction].includes(type);
  const isGeneratorFn = [typeNames.GeneratorFunction, typeNames.AsyncGeneratorFunction].includes(type);
  const isAnonFn = isAnonymousFunction(data);
  const isArrowFn = isArrowFunction(data);
  return /*#__PURE__*/React.createElement("i", null, !hideDataTypes && /*#__PURE__*/React.createElement("span", {
    style: {
      color: typeColor
    }
  }, isAsyncFn ? 'async ' : '', /*#__PURE__*/React.createElement("b", null, "\u0192"), isGeneratorFn ? /*#__PURE__*/React.createElement("b", {
    style: {
      color: arrowColor
    }
  }, '* ') : ' '), /*#__PURE__*/React.createElement("span", null, isAnonFn ? '()' : isArrowFn ? /*#__PURE__*/React.createElement(React.Fragment, null, '()', /*#__PURE__*/React.createElement("span", {
    style: {
      color: arrowColor
    }
  }, "\xA0=>\xA0"), '{}') : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    style: {
      color: nameColor
    }
  }, data.name), ' ()')));
}

export function Value(_ref2) {
  let {
    data,
    ...props
  } = _ref2;
  const theme = useRecoilValue(themeState);
  const {
    hideDataTypes
  } = useRecoilValue(optionsState);
  const nullOrUndefinedStyle = {
    textTransform: 'uppercase',
    fontWeight: 'bolder',
    fontSize: `${theme.fontSize - 3}px`,
    lineHeight: `${theme.fontSize}px`,
    padding: '2px 4px',
    borderRadius: 2,
    alignSelf: 'center'
  };
  const vdom = useMemo(() => {
    var _data$size;

    if (data !== null && data !== void 0 && data[Symbol_customValue]) {
      return /*#__PURE__*/React.createElement("span", null, data === null || data === void 0 ? void 0 : data[Symbol_customValue]());
    }

    switch (getTypeOf(data)) {
      case typeNames.URL:
        return /*#__PURE__*/React.createElement("a", {
          href: data,
          style: {
            color: theme.valueURLColor,
            fontStyle: 'italic',
            ...props.style
          }
        }, data);

      case typeNames.string:
        return /*#__PURE__*/React.createElement("span", {
          style: {
            color: theme.valueStringColor,
            ...props.style
          }
        }, "'", data, "'");

      case typeNames.bigint:
        return /*#__PURE__*/React.createElement("span", {
          style: {
            color: theme.valueNumberColor,
            ...props.style
          }
        }, data, "n");

      case typeNames.number:
        return /*#__PURE__*/React.createElement("span", {
          style: {
            color: theme.valueNumberColor,
            ...props.style
          }
        }, data);

      case typeNames.boolean:
        return /*#__PURE__*/React.createElement("span", {
          style: {
            color: theme.valueBooleanColor,
            ...props.style
          }
        }, String(data));

      case typeNames.symbol:
        return /*#__PURE__*/React.createElement("i", {
          style: props.style
        }, /*#__PURE__*/React.createElement("span", {
          style: {
            color: theme.valueTypeColor
          }
        }, "Symbol"), `(`, /*#__PURE__*/React.createElement("span", {
          style: {
            color: theme.valueStringColor
          }
        }, data.description), `)`);

      case typeNames.undefined:
        return /*#__PURE__*/React.createElement("span", {
          style: {
            color: theme.valueUndefinedColor,
            backgroundColor: theme.valueUndefinedBackgroundColor,
            ...nullOrUndefinedStyle,
            ...props.style
          }
        }, String(data));

      case typeNames.null:
        return /*#__PURE__*/React.createElement("span", {
          style: {
            color: theme.valueNullColor,
            backgroundColor: theme.valueNullBackgroundColor,
            ...nullOrUndefinedStyle,
            ...props.style
          }
        }, String(data));

      case typeNames.Function:
      case typeNames.AsyncFunction:
      case typeNames.AsyncGeneratorFunction:
      case typeNames.GeneratorFunction:
        return /*#__PURE__*/React.createElement(FunctionValue, {
          data: data,
          typeColor: theme.valueTypeColor,
          nameColor: theme.valueFunctionColor,
          arrowColor: theme.valueFunctionArrowColor,
          hideDataTypes: hideDataTypes,
          style: props.style
        });

      case typeNames.Date:
        return /*#__PURE__*/React.createElement("i", {
          style: {
            color: theme.valueDateColor,
            ...props.style
          }
        }, data.toString());

      case typeNames.RegExp:
        return /*#__PURE__*/React.createElement("i", {
          style: {
            color: theme.valueRegExpColor,
            ...props.style
          }
        }, data.toString());

      case typeNames.Map:
      case typeNames.Set:
      case typeNames.Array:
        return /*#__PURE__*/React.createElement("span", {
          style: props.style
        }, /*#__PURE__*/React.createElement("span", {
          style: {
            color: theme.valueTypeColor
          }
        }, getTypeOf(data)), `(`, /*#__PURE__*/React.createElement("span", {
          style: {
            color: theme.valueNumberColor
          }
        }, (_data$size = data.size) !== null && _data$size !== void 0 ? _data$size : data.length), `)`);

      case typeNames.Object:
        return /*#__PURE__*/React.createElement("span", {
          style: {
            color: theme.valueColor,
            ...props.style
          }
        }, `{`, /*#__PURE__*/React.createElement("span", {
          style: {
            color: theme.color
          }
        }, Object.keys(data).length > 0 ? '…' : ''), `}`);

      default:
        return /*#__PURE__*/React.createElement("span", {
          style: {
            color: theme.valueColor,
            ...props.style
          }
        }, (() => {
          try {
            return String(data);
          } catch (e) {
            console.error(e);
            return null;
          }
        })());
    }
  }, [data, hideDataTypes, theme]);
  return vdom !== null && vdom !== void 0 ? vdom : null;
}